import React, { useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import gsap from "gsap";

const DamagePolicy = () => {
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      heroRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
    );

    gsap.fromTo(
      contentRef.current.querySelectorAll(".policy-section"),
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: "power3.out",
      }
    );
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <div className="bg-black">
        <Navbar />
      </div>
      
      {/* Hero Section */}
      <div ref={heroRef} className="relative py-20 px-4 text-center text-white bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-6xl mb-6">⚠️</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-[#A88006]">
            Damage & Defective Product Policy
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Our commitment to quality and customer satisfaction for damaged or defective products.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div ref={contentRef} className="max-w-4xl mx-auto px-4 py-16">
        <div className="space-y-8">
          {/* Policy Overview */}
          <div className="policy-section bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
            <h2 className="text-2xl font-bold text-[#A88006] mb-4">Policy Overview</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              At Gift of God Fragrances, we stand behind the quality of our products. If you receive a damaged 
              or defective item, we will work quickly to resolve the issue and ensure your complete satisfaction.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This policy covers products that arrive damaged during shipping or have manufacturing defects 
              that affect the product's functionality or appearance.
            </p>
          </div>

          {/* What Qualifies */}
          <div className="policy-section bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
            <h2 className="text-2xl font-bold text-[#A88006] mb-4">What Qualifies as Damaged or Defective</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-black mb-3">Shipping Damage</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Broken or cracked bottles</li>
                  <li>• Leaking or spilled contents</li>
                  <li>• Damaged packaging affecting product integrity</li>
                  <li>• Missing components or accessories</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-black mb-3">Manufacturing Defects</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Faulty spray mechanisms</li>
                  <li>• Incorrect product contents</li>
                  <li>• Contaminated or altered fragrance</li>
                  <li>• Defective bottle caps or pumps</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Reporting Process */}
          <div className="policy-section bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
            <h2 className="text-2xl font-bold text-[#A88006] mb-4">How to Report Damage or Defects</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#A88006] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="text-black font-semibold mb-2">Contact Us Immediately</h4>
                  <p className="text-gray-700 text-sm">
                    Report the issue within 48 hours of delivery by emailing info@giftofgodfragrances.com 
                    or calling (848) 667-0850.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#A88006] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="text-black font-semibold mb-2">Provide Documentation</h4>
                  <p className="text-gray-700 text-sm">
                    Include your order number, photos of the damaged item, and a description of the issue.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#A88006] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="text-black font-semibold mb-2">Await Instructions</h4>
                  <p className="text-gray-700 text-sm">
                    Our customer service team will review your case and provide resolution instructions within 24 hours.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Resolution Options */}
          <div className="policy-section bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg">
            <h2 className="text-2xl font-bold text-[#A88006] mb-4">Resolution Options</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gradient-to-r from-[#A88006]/10 to-[#9F7800]/10 rounded-lg border border-[#A88006]/30">
                <div className="text-2xl mb-3">🔄</div>
                <h4 className="text-black font-semibold mb-2">Replacement</h4>
                <p className="text-gray-700 text-sm">
                  We'll send a replacement product at no additional cost with expedited shipping.
                </p>
              </div>
              <div className="text-center p-4 bg-gradient-to-r from-[#A88006]/10 to-[#9F7800]/10 rounded-lg border border-[#A88006]/30">
                <div className="text-2xl mb-3">💰</div>
                <h4 className="text-black font-semibold mb-2">Full Refund</h4>
                <p className="text-gray-700 text-sm">
                  Receive a complete refund including original shipping costs to your payment method.
                </p>
              </div>
              <div className="text-center p-4 bg-gradient-to-r from-[#A88006]/10 to-[#9F7800]/10 rounded-lg border border-[#A88006]/30">
                <div className="text-2xl mb-3">💳</div>
                <h4 className="text-black font-semibold mb-2">Store Credit</h4>
                <p className="text-gray-700 text-sm">
                  Receive store credit with a 10% bonus for future purchases on our website.
                </p>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="policy-section bg-gradient-to-r from-[#A88006]/10 to-[#9F7800]/10 p-8 rounded-xl border border-[#A88006]/20">
            <h2 className="text-2xl font-bold text-[#A88006] mb-4">Important Notes</h2>
            <div className="space-y-3 text-gray-700">
              <p>• <strong>No Return Required:</strong> In most cases, you won't need to return the damaged item.</p>
              <p>• <strong>Fast Resolution:</strong> We prioritize damage claims and aim to resolve them within 24-48 hours.</p>
              <p>• <strong>No Additional Costs:</strong> All replacement shipping and handling fees are covered by us.</p>
              <p>• <strong>Quality Assurance:</strong> We inspect all products before shipping to minimize damage occurrences.</p>
              <p>• <strong>Carrier Claims:</strong> We handle all insurance claims with shipping carriers on your behalf.</p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="policy-section bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg text-center">
            <h2 className="text-2xl font-bold text-black mb-4">Need to Report an Issue?</h2>
            <p className="text-gray-700 mb-6">
              Our customer service team is ready to help resolve any damage or defect issues quickly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@giftofgodfragrances.com"
                className="bg-gradient-to-r from-[#A88006] to-[#9F7800] text-white font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-all"
              >
                Email Support
              </a>
              <a
                href="tel:+18486670850"
                className="border border-[#A88006] text-[#A88006] font-semibold px-6 py-3 rounded-lg hover:bg-[#A88006]/10 transition-all"
              >
                Call (848) 667-0850
              </a>
            </div>
            <p className="text-gray-600 text-sm mt-4">
              Available Monday - Friday, 10 AM - 6 PM EST
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DamagePolicy;